import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Table, Card, Button, Space, Typography, Grid, Empty, Tag, Popconfirm, Tooltip, message, Modal, DatePicker } from 'antd';
import { PlusOutlined, SyncOutlined, EditOutlined, DeleteOutlined, UnlockOutlined, LockOutlined } from '@ant-design/icons';
import { Translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import {
  closePeriode,
  createPeriode,
  deletePeriode,
  getPeriodes,
  openPeriode,
  reopenPeriode,
  updatePeriode,
} from './periode-evaluation.reducer';
import { getEcole } from '../ecole/ecole.reducer';
import { getCycles } from 'app/entities/cycle/cycle.reducer';
import { getAnneeScolaire, getEcoleActiveAnneeScolaire } from 'app/entities/annee-scolaire/annee-scolaire.reducer';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { StatutPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';
import { IPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';
import moment, { Moment } from 'moment';
import { RangeValue } from 'rc-picker/lib/interface';

const { Title } = Typography;
const { useBreakpoint } = Grid;
const CREATE_PERIOD_DEFAULT_VALUES = {};

export const PeriodeEvaluation = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId?: string }>) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(state => state.periodeEvaluation.loading);
  const updating = useAppSelector(state => state.periodeEvaluation.updating);
  const periodesByCycle = useAppSelector(state => state.periodeEvaluation.entitiesByCycle);
  const cycles = useAppSelector(state => state.cycle.entities);
  const anneeScolaire = useAppSelector(state => state.anneeScolaire.entityActive);
  const canOpenPeriode = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.DIRECTEUR, AUTHORITIES.DIRECTEUR_ETUDE])
  );
  const canReopenPeriode = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.DIRECTEUR])
  );
  const canClosePeriode = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.DIRECTEUR])
  );
  const screens = useBreakpoint();

  const [loadingAll, setLoadingAll] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Moment | null, Moment | null] | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedPeriode, setEditedPeriode] = useState<IPeriodeEvaluation | null>(null);
  const [editDateRange, setEditDateRange] = useState<[Moment | null, Moment | null] | null>(null);
  const [editDateRangeError, setEditDateRangeError] = useState<string | null>(null);
  const [periodeToDelete, setPeriodeToDelete] = useState<IPeriodeEvaluation | null>(null);
  const selectedAnneeScolaireId = props.match.params.anneeScolaireId || anneeScolaire?.id;

  useEffect(() => {
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(getCycles(props.match.params.ecoleid));
    if (props.match.params.anneeScolaireId) {
      dispatch(getAnneeScolaire(props.match.params.anneeScolaireId));
    } else {
      dispatch(getEcoleActiveAnneeScolaire(props.match.params.ecoleid));
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      if (!selectedAnneeScolaireId || cycles.length === 0) {
        return;
      }

      setLoadingAll(true);
      try {
        await Promise.all(
          cycles.map(cycle =>
            dispatch(
              getPeriodes({
                ecoleId: props.match.params.ecoleid,
                anneeScolaireId: selectedAnneeScolaireId,
                cycleId: cycle.id,
              })
            ).unwrap()
          )
        );
      } finally {
        setLoadingAll(false);
      }
    };

    loadAll();
  }, [selectedAnneeScolaireId, cycles, dispatch, props.match.params.ecoleid]);

  const handleSyncList = () => {
    if (!selectedAnneeScolaireId || cycles.length === 0) {
      return;
    }

    const reload = async () => {
      setLoadingAll(true);
      try {
        await Promise.all(
          cycles.map(cycle =>
            dispatch(
              getPeriodes({
                ecoleId: props.match.params.ecoleid,
                anneeScolaireId: selectedAnneeScolaireId,
                cycleId: cycle.id,
              })
            ).unwrap()
          )
        );
      } finally {
        setLoadingAll(false);
      }
    };

    reload();
  };

  const closeCreateModal = () => {
    if (!updating) {
      setCreateModalOpen(false);
      setSelectedCycleId(null);
      setDateRange(null);
      setDateRangeError(null);
    }
  };

  const handleDateRangeChange = (dates: RangeValue<Moment>) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRange(null);
      return;
    }
    setDateRange([dates[0], dates[1]]);
    setDateRangeError(null);
  };

  const disabledDate = (current: Moment) => {
    const yearStart = anneeScolaire?.dateDebut ? moment(anneeScolaire.dateDebut) : null;
    const yearEnd = anneeScolaire?.dateCloture ? moment(anneeScolaire.dateCloture) : null;
    return Boolean((yearStart && current.isBefore(yearStart, 'day')) || (yearEnd && current.isAfter(yearEnd, 'day')));
  };

  const handleCreatePeriode = async values => {
    if (!selectedAnneeScolaireId || !selectedCycleId || !dateRange?.[0] || !dateRange?.[1]) {
      setDateRangeError('Veuillez sélectionner une plage de dates.');
      return;
    }
    const result = await dispatch(
      createPeriode({
        ...values,
        dateDebut: dateRange[0].format('YYYY-MM-DD'),
        dateFin: dateRange[1].format('YYYY-MM-DD'),
        ecoleId: props.match.params.ecoleid,
        anneeScolaireId: selectedAnneeScolaireId,
        cycleId: selectedCycleId,
      })
    );
    if (createPeriode.fulfilled.match(result)) {
      closeCreateModal();
      message.success('Période créée');
    }
  };

  const openEditModal = (periode: IPeriodeEvaluation) => {
    setEditedPeriode(periode);
    setEditDateRange([periode.dateDebut ? moment(periode.dateDebut) : null, periode.dateFin ? moment(periode.dateFin) : null]);
    setEditDateRangeError(null);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (updating) return;
    setEditModalOpen(false);
    setEditedPeriode(null);
    setEditDateRange(null);
    setEditDateRangeError(null);
  };

  const handleEditDateRangeChange = (dates: RangeValue<Moment>) => {
    if (!dates || !dates[0] || !dates[1]) {
      setEditDateRange(null);
      return;
    }
    setEditDateRange([dates[0], dates[1]]);
    setEditDateRangeError(null);
  };

  const handleUpdatePeriode = async values => {
    if (!editedPeriode || !selectedAnneeScolaireId || !editDateRange?.[0] || !editDateRange?.[1]) {
      setEditDateRangeError('Veuillez sélectionner une plage de dates.');
      return;
    }

    try {
      await dispatch(
        updatePeriode({
          ...editedPeriode,
          ...values,
          dateDebut: editDateRange[0].format('YYYY-MM-DD'),
          dateFin: editDateRange[1].format('YYYY-MM-DD'),
          ecoleId: props.match.params.ecoleid,
          anneeScolaireId: selectedAnneeScolaireId,
          cycleId: editedPeriode.cycleId,
        })
      ).unwrap();
      setEditModalOpen(false);
      setEditedPeriode(null);
      setEditDateRange(null);
      message.success('Période modifiée');
    } catch (e: any) {
      message.error(e?.response?.data?.title || 'Impossible de modifier la période');
    }
  };

  const handleDeletePeriode = async () => {
    if (!periodeToDelete?.id || !periodeToDelete.cycleId || !selectedAnneeScolaireId) return;

    try {
      await dispatch(
        deletePeriode({
          id: periodeToDelete.id,
          ecoleId: props.match.params.ecoleid,
          anneeScolaireId: selectedAnneeScolaireId,
          cycleId: periodeToDelete.cycleId,
        })
      ).unwrap();
      setPeriodeToDelete(null);
      message.success('Période supprimée');
    } catch (e: any) {
      message.error(e?.response?.data?.title || 'Impossible de supprimer la période');
    }
  };

  const handleOpenPeriode = async (periode: any) => {
    if (!selectedAnneeScolaireId || !periode?.cycleId) return;
    try {
      const action = periode.statut === StatutPeriodeEvaluation.FERMEE ? reopenPeriode : openPeriode;
      await dispatch(
        action({
          id: periode.id,
          ecoleId: props.match.params.ecoleid,
          anneeScolaireId: selectedAnneeScolaireId,
          cycleId: periode.cycleId,
        })
      ).unwrap();
      message.success(periode.statut === StatutPeriodeEvaluation.FERMEE ? 'Période rouverte' : 'Période ouverte');
    } catch (e: any) {
      message.error(e?.response?.data?.title || "Impossible d'ouvrir la période");
    }
  };

  const handleClosePeriode = async (periode: any) => {
    if (!selectedAnneeScolaireId || !periode?.cycleId) return;
    try {
      await dispatch(
        closePeriode({
          id: periode.id,
          ecoleId: props.match.params.ecoleid,
          anneeScolaireId: selectedAnneeScolaireId,
          cycleId: periode.cycleId,
        })
      ).unwrap();
      message.success('Période fermée');
    } catch (e: any) {
      message.error(e?.response?.data?.title || 'Impossible de fermer la période');
    }
  };

  const renderStatut = (statut?: StatutPeriodeEvaluation) => {
    if (statut === StatutPeriodeEvaluation.OUVERTE) return <Tag color="green">Ouverte</Tag>;
    if (statut === StatutPeriodeEvaluation.FERMEE) return <Tag color="red">Fermée</Tag>;
    return <Tag color="blue">Planifiée</Tag>;
  };

  const columns = [
    { title: 'Nom', dataIndex: 'nom', key: 'nom' },
    { title: 'Début', dataIndex: 'dateDebut', key: 'dateDebut' },
    { title: 'Fin', dataIndex: 'dateFin', key: 'dateFin' },
    {
      title: 'Statut',
      dataIndex: 'statut',
      key: 'statut',
      render: statut => renderStatut(statut),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: screens.xs ? 150 : 320,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip
            title={record.statut === StatutPeriodeEvaluation.FERMEE ? 'Rouvrez la période avant de la modifier' : 'Modifier la période'}
          >
            <span>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
                size={screens.xs ? 'small' : 'middle'}
                disabled={record.statut === StatutPeriodeEvaluation.FERMEE}
              />
            </span>
          </Tooltip>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => setPeriodeToDelete(record)}
            size={screens.xs ? 'small' : 'middle'}
          />
          {(canOpenPeriode || canReopenPeriode || canClosePeriode) && (
            <>
              {((canOpenPeriode && record.statut === StatutPeriodeEvaluation.PLANIFIEE) ||
                (canReopenPeriode && record.statut === StatutPeriodeEvaluation.FERMEE)) &&
                (() => {
                  const cycleAlreadyHasOpen = (periodesByCycle[record.cycleId] || []).some(
                    p => p.id !== record.id && p.statut === StatutPeriodeEvaluation.OUVERTE
                  );
                  const isReopening = record.statut === StatutPeriodeEvaluation.FERMEE;
                  return (
                    <Popconfirm
                      title={isReopening ? 'Rouvrir cette période ?' : 'Ouvrir cette période ?'}
                      okText={isReopening ? 'Rouvrir' : 'Ouvrir'}
                      cancelText="Annuler"
                      onConfirm={() => handleOpenPeriode(record)}
                      disabled={cycleAlreadyHasOpen}
                    >
                      <Tooltip
                        title={
                          cycleAlreadyHasOpen
                            ? 'Une période est déjà ouverte pour ce cycle'
                            : isReopening
                            ? 'Rouvrir la période'
                            : 'Ouvrir la période'
                        }
                      >
                        <Button
                          type="text"
                          icon={<UnlockOutlined style={{ color: cycleAlreadyHasOpen ? '#d9d9d9' : '#52c41a' }} />}
                          size={screens.xs ? 'small' : 'middle'}
                          disabled={cycleAlreadyHasOpen}
                        />
                      </Tooltip>
                    </Popconfirm>
                  );
                })()}
              {canClosePeriode && record.statut === StatutPeriodeEvaluation.OUVERTE && (
                <Popconfirm
                  title="Fermer cette période ?"
                  okText="Fermer"
                  cancelText="Annuler"
                  onConfirm={() => handleClosePeriode(record)}
                >
                  <Tooltip title="Fermer la période">
                    <Button
                      type="text"
                      danger
                      icon={<LockOutlined style={{ color: '#ff4d4f' }} />}
                      size={screens.xs ? 'small' : 'middle'}
                    />
                  </Tooltip>
                </Popconfirm>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ margin: screens.xs ? '16px 8px' : '24px 24px' }} bodyStyle={{ padding: screens.xs ? '0 8px 16px' : '0 24px 24px' }}>
      <div
        style={{
          padding: screens.xs ? '16px 0' : '24px 0',
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'flex-start' : 'center',
          gap: screens.xs ? '16px' : '0',
        }}
      >
        <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '1.5rem' : '2rem' }}>
          <Translate contentKey="korischoolApp.periodeEvaluation.home.title">Périodes d`&apos;`évaluation</Translate>
        </Title>
        <Space>
          <Button
            onClick={handleSyncList}
            className="d-flex align-items-center"
            disabled={loadingAll}
            size={screens.xs ? 'small' : 'middle'}
          >
            <SyncOutlined spin={loadingAll} />
            <Translate contentKey="korischoolApp.periodeEvaluation.home.refreshListLabel">Refresh List</Translate>
          </Button>
        </Space>
      </div>

      {!selectedAnneeScolaireId ? (
        <Empty description="Sélectionnez une année scolaire active" />
      ) : cycles.length === 0 ? (
        <Empty description="Aucun cycle trouvé" />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {cycles.map(cycle => (
            <Card
              key={cycle.id}
              title={`Cycle: ${cycle.nom}`}
              extra={
                <Tooltip title="Créer une période d'évaluation">
                  <Button
                    type="text"
                    aria-label="Créer une période d'évaluation"
                    icon={<PlusOutlined style={{ fontSize: screens.xs ? 18 : 20 }} />}
                    onClick={() => {
                      setSelectedCycleId(cycle.id);
                      setCreateModalOpen(true);
                    }}
                    disabled={!selectedAnneeScolaireId}
                    size={screens.xs ? 'small' : 'middle'}
                  />
                </Tooltip>
              }
              bodyStyle={{ paddingTop: 8 }}
            >
              <Table
                dataSource={periodesByCycle[cycle.id] || []}
                columns={columns}
                rowKey="id"
                pagination={false}
                loading={loading || loadingAll}
                locale={{
                  emptyText: <Translate contentKey="korischoolApp.periodeEvaluation.home.notFound">No periods found</Translate>,
                }}
                bordered
                size="middle"
                style={{ backgroundColor: 'white' }}
                scroll={{ x: screens.xs ? 'true' : 'false' }}
              />
            </Card>
          ))}
        </Space>
      )}

      <Modal
        title={`Créer une période d'évaluation — ${cycles.find(cycle => cycle.id === selectedCycleId)?.nom || ''}`}
        visible={createModalOpen}
        onCancel={closeCreateModal}
        footer={null}
        destroyOnClose
        maskClosable={!updating}
        closable={!updating}
      >
        <ValidatedForm defaultValues={CREATE_PERIOD_DEFAULT_VALUES} onSubmit={handleCreatePeriode}>
          <ValidatedField label="Nom" id="periode-create-nom" name="nom" type="text" />
          <div className="form-group">
            <label htmlFor="periode-create-date-range">Date</label>
            <DatePicker.RangePicker
              id="periode-create-date-range"
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder={['Date de début', 'Date de fin']}
              separator="au"
              allowClear
              disabledDate={disabledDate}
            />
            {dateRangeError && <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>{dateRangeError}</div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={closeCreateModal} disabled={updating}>
              Annuler
            </Button>
            <Button htmlType="submit" loading={updating} style={{ backgroundColor: '#F28E19', color: 'white' }}>
              Enregistrer
            </Button>
          </div>
        </ValidatedForm>
      </Modal>

      <Modal
        title={`Modifier la période d'évaluation — ${cycles.find(cycle => cycle.id === editedPeriode?.cycleId)?.nom || ''}`}
        visible={editModalOpen}
        onCancel={closeEditModal}
        footer={null}
        destroyOnClose
        maskClosable={!updating}
        closable={!updating}
      >
        {editedPeriode && (
          <ValidatedForm key={editedPeriode.id} defaultValues={{ nom: editedPeriode.nom }} onSubmit={handleUpdatePeriode}>
            <ValidatedField label="Nom" id="periode-edit-nom" name="nom" type="text" />
            <div className="form-group">
              <label htmlFor="periode-edit-date-range">Date</label>
              <DatePicker.RangePicker
                id="periode-edit-date-range"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                value={editDateRange}
                onChange={handleEditDateRangeChange}
                placeholder={['Date de début', 'Date de fin']}
                separator="au"
                allowClear
                disabledDate={disabledDate}
              />
              {editDateRangeError && (
                <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>{editDateRangeError}</div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={closeEditModal} disabled={updating}>
                Annuler
              </Button>
              <Button htmlType="submit" loading={updating} style={{ backgroundColor: '#F28E19', color: 'white' }}>
                Enregistrer
              </Button>
            </div>
          </ValidatedForm>
        )}
      </Modal>

      <Modal
        title="Supprimer la période d'évaluation"
        visible={Boolean(periodeToDelete)}
        onCancel={() => !updating && setPeriodeToDelete(null)}
        onOk={handleDeletePeriode}
        okText="Supprimer"
        cancelText="Annuler"
        okButtonProps={{ danger: true, loading: updating }}
        cancelButtonProps={{ disabled: updating }}
        maskClosable={!updating}
        closable={!updating}
      >
        Voulez-vous vraiment supprimer la période « {periodeToDelete?.nom} » ?
      </Modal>
    </Card>
  );
};

export default PeriodeEvaluation;
